'use client';

import * as React from 'react';
import * as SelectPrimitive from '@radix-ui/react-select';
import { cn } from '../../utils/cn/cn';
import { Label } from '../Label/Label';
import { Notification } from '../Notification/Notification';

const SelectRoot = SelectPrimitive.Root;

const SelectTrigger = React.forwardRef<
  React.ComponentRef<typeof SelectPrimitive.Trigger>,
  React.ComponentPropsWithoutRef<typeof SelectPrimitive.Trigger>
>(({ className, children, ...props }, ref) => (
  <SelectPrimitive.Trigger
    ref={ref}
    className={cn(
      'data-[state=open]:border-color-primary' +
        ' group flex h-[44px] w-full items-center justify-between rounded-sm border p-1' +
        ' disabled:opacity-35 [&>span]:line-clamp-1',
      className,
    )}
    {...props}
  >
    {children}
    <SelectPrimitive.Icon asChild>
      <span className="icon-emp-arrow-medium-down h-4 w-4 shrink-0 group-data-[state=open]:rotate-180" />
    </SelectPrimitive.Icon>
  </SelectPrimitive.Trigger>
));
SelectTrigger.displayName = SelectPrimitive.Trigger.displayName;

const SelectScrollUpButton = React.forwardRef<
  React.ComponentRef<typeof SelectPrimitive.ScrollUpButton>,
  React.ComponentPropsWithoutRef<typeof SelectPrimitive.ScrollUpButton>
>(({ className, ...props }, ref) => (
  <SelectPrimitive.ScrollUpButton
    ref={ref}
    className={cn('flex cursor-default items-center justify-center py-1', className)}
    {...props}
  >
    <span className="icon-emp-arrow-mini-up h-4 w-4 shrink-0" />
  </SelectPrimitive.ScrollUpButton>
));
SelectScrollUpButton.displayName = SelectPrimitive.ScrollUpButton.displayName;

const SelectScrollDownButton = React.forwardRef<
  React.ComponentRef<typeof SelectPrimitive.ScrollDownButton>,
  React.ComponentPropsWithoutRef<typeof SelectPrimitive.ScrollDownButton>
>(({ className, ...props }, ref) => (
  <SelectPrimitive.ScrollDownButton
    ref={ref}
    className={cn('flex cursor-default items-center justify-center py-1', className)}
    {...props}
  >
    <span className="icon-emp-arrow-mini-down h-4 w-4 shrink-0" />
  </SelectPrimitive.ScrollDownButton>
));
SelectScrollDownButton.displayName = SelectPrimitive.ScrollDownButton.displayName;

const SelectContent = React.forwardRef<
  React.ComponentRef<typeof SelectPrimitive.Content>,
  React.ComponentPropsWithoutRef<typeof SelectPrimitive.Content>
>(({ className, children, position = 'popper', ...props }, ref) => (
  <SelectPrimitive.Portal>
    <SelectPrimitive.Content
      ref={ref}
      className={cn(
        'bg-color-background-base border-color-grey text-color-text relative z-50 max-h-[24rem] min-w-[8rem] overflow-hidden rounded-sm border shadow-md' +
          ' data-[state=open]:animate-in data-[state=open]:fade-in-0 data-[state=open]:zoom-in-95' +
          ' data-[state=closed]:zoom-out-95 data-[state=closed]:animate-out data-[state=closed]:fade-out-0' +
          ' data-[side=bottom]:slide-in-from-top-2 data-[side=left]:slide-in-from-right-2 data-[side=right]:slide-in-from-left-2 data-[side=top]:slide-in-from-bottom-2',
        position === 'popper' &&
          'data-[side=bottom]:translate-y-1 data-[side=left]:-translate-x-1 data-[side=right]:translate-x-1 data-[side=top]:-translate-y-1',
        className,
      )}
      position={position}
      {...props}
    >
      <SelectScrollUpButton />
      <SelectPrimitive.Viewport
        className={cn(
          'py-1',
          position === 'popper' &&
            'h-[var(--radix-select-trigger-height)] w-full min-w-[var(--radix-select-trigger-width)]',
        )}
      >
        {children}
      </SelectPrimitive.Viewport>
      <SelectScrollDownButton />
    </SelectPrimitive.Content>
  </SelectPrimitive.Portal>
));
SelectContent.displayName = SelectPrimitive.Content.displayName;

const SelectLabel = React.forwardRef<
  React.ComponentRef<typeof SelectPrimitive.Label>,
  React.ComponentPropsWithoutRef<typeof SelectPrimitive.Label>
>(({ className, ...props }, ref) => (
  <SelectPrimitive.Label
    ref={ref}
    className={cn('text-size-body1 py-1.5 pr-2 pl-[2rem] font-semibold', className)}
    {...props}
  />
));
SelectLabel.displayName = SelectPrimitive.Label.displayName;

const SelectItem = React.forwardRef<
  React.ComponentRef<typeof SelectPrimitive.Item>,
  React.ComponentPropsWithoutRef<typeof SelectPrimitive.Item>
>(({ className, children, ...props }, ref) => (
  <SelectPrimitive.Item
    ref={ref}
    className={cn(
      'focus:bg-color-grey-light data-[state=checked]:bg-color-grey-light ' +
        ' relative flex w-full cursor-default items-center py-1.5 pr-2 pl-1 outline-hidden select-none',
      className,
    )}
    {...props}
  >
    <SelectPrimitive.ItemText>{children}</SelectPrimitive.ItemText>
  </SelectPrimitive.Item>
));
SelectItem.displayName = SelectPrimitive.Item.displayName;

const SelectSeparator = React.forwardRef<
  React.ComponentRef<typeof SelectPrimitive.Separator>,
  React.ComponentPropsWithoutRef<typeof SelectPrimitive.Separator>
>(({ className, ...props }, ref) => (
  <SelectPrimitive.Separator ref={ref} className={cn('mx-1 my-1 h-px', className)} {...props} />
));
SelectSeparator.displayName = SelectPrimitive.Separator.displayName;

const SelectValue = SelectPrimitive.Value;
export interface SelectProps
  extends React.ComponentPropsWithoutRef<typeof SelectRoot>,
    Pick<SelectPrimitive.SelectTriggerProps, 'id'> {
  error?: boolean;
  helperText?: string;
  children: React.ReactNode;
  label?: string;
  triggerProps?: React.ComponentProps<typeof SelectTrigger>;
  placeholder?: string;
  containerClassName?: string;
}

const Select = React.forwardRef(
  (
    {
      error,
      helperText,
      children,
      label,
      id,
      triggerProps,
      placeholder,
      containerClassName,
      ...props
    }: SelectProps,
    ref: React.Ref<React.ComponentRef<typeof SelectPrimitive.Trigger>>,
  ) => {
    const fallBackId = React.useId();
    return (
      <div data-testid="select-wrapper-ui" className={containerClassName}>
        {label && (
          <Label htmlFor={id || fallBackId} className="text-size-body2">
            {label}
          </Label>
        )}

        <SelectRoot {...props}>
          <SelectTrigger
            ref={ref}
            id={id || fallBackId}
            {...triggerProps}
            className={
              error
                ? 'border-color-error focus-visible:input-error focus-visible:outline-color-error border-2'
                : 'border-color-grey-dark focus-visible:input-active focus-visible:outline-color-primary'
            }
          >
            <SelectValue placeholder={placeholder} />
          </SelectTrigger>
          {children}
        </SelectRoot>
        {helperText && (
          <Notification severity={error ? 'error' : 'info'} variant="feedback" className="mt-0.5">
            {helperText}
          </Notification>
        )}
      </div>
    );
  },
);

export {
  Select,
  SelectRoot,
  SelectValue,
  SelectTrigger,
  SelectContent,
  SelectLabel,
  SelectItem,
  SelectSeparator,
  SelectScrollUpButton,
  SelectScrollDownButton,
};
